/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Dispatch } from "react";
import { ApolloError } from "@apollo/client";
import { ColumnDef } from "@tanstack/react-table";
import { When } from "react-if";
import { Spacer, theme } from "@nordcloud/gnui";
import {
  ErrorWrap,
  LoaderWrap,
  CostAnalysisCostTable,
  ChartPeriodData,
  TableData,
  ChartType,
  Period,
  LinkParameters,
} from "~/components";
import { TotalCosts } from "./components";

const ANOMALY_TOOLTIP =
  "Cost data may be not complete. Precise cost analysis estimation is not yet available.";

type Props = {
  period: Period;
  startDate: string;
  endDate: string;
  tableData: TableData[];
  chartType: ChartType;
  total: number;
  columns: ColumnDef<TableData>[];
  isLoading: boolean;
  showCostTable: boolean;
  hasFooter: boolean;
  linkParameters?: LinkParameters;
  tableError?: ApolloError;
  getSubRows?: (
    originalRow: TableData,
    index: number
  ) => TableData[] | undefined;
  setChangedThreshold?: Dispatch<number>;
  isEmptyData?: boolean;
};

export function ApplicationCostTableData({
  period,
  startDate,
  endDate,
  tableData,
  chartType,
  total,
  columns,
  isLoading,
  showCostTable,
  hasFooter,
  tableError,
  linkParameters,
  getSubRows,
  setChangedThreshold,
  isEmptyData = false,
}: Props) {
  return (
    <>
      <When condition={shouldShowPeriod(chartType, period)}>
        <ChartPeriodData
          period={period}
          startDate={startDate}
          endDate={endDate}
          chartType={chartType}
        />
      </When>
      <Spacer height={theme.spacing.spacing04} />
      <LoaderWrap inContent loading={isLoading}>
        <TotalCosts
          tableData={tableData}
          chartTypeValue={chartType}
          total={total}
          showTotalCost={shouldShowTotalCost(chartType, period)}
          setChangedThreshold={setChangedThreshold}
          isEmptyData={isEmptyData}
          linkParameters={linkParameters}
          tooltipText={
            chartType === ChartType.ANOMALY_COST ? ANOMALY_TOOLTIP : undefined
          }
        />
        <When condition={showCostTable}>
          <ErrorWrap error={tableError}>
            <div css={{ width: "100%", overflow: "scroll" }}>
              <CostAnalysisCostTable
                columns={columns}
                data={tableData}
                hasFooter={hasFooter}
                getSubRows={getSubRows}
                period={period}
              />
            </div>
          </ErrorWrap>
        </When>
      </LoaderWrap>
    </>
  );
}

function shouldShowTotalCost(chartType: ChartType, period: Period) {
  const showTotalForMonth = [
    ChartType.COST_PER_ENVIRONMENT,
    ChartType.COST_PER_CATEGORY,
  ].includes(chartType);

  if (period === Period.MONTH && showTotalForMonth) {
    return true;
  }

  return chartType === ChartType.ANOMALY_COST;
}

function shouldShowPeriod(chartType: ChartType, period: Period) {
  if (period !== Period.MONTH && period === Period.RANGE) {
    return true;
  }

  return [
    ChartType.COST_PER_ENVIRONMENT,
    ChartType.COST_PER_CATEGORY,
    ChartType.ANOMALY_COST,
  ].includes(chartType);
}
